import React from 'react';
import cn from 'classnames';
import { generatePath, NavLink, useHistory } from 'react-router-dom';

import { useAppDispatch, useAppSelector, useTranslation } from 'client/hooks';
import * as routes from 'client/constants/routes';
import { setMenuState, setPopup } from 'client/redux/ui';
import { selectLang } from 'client/redux/settings/selectors';
import { selectAuth } from 'client/redux/user/selectors';

import { getMenuLinks } from '../../Menu/helpers';

import css from './ControlPanel.module.scss';

interface Props {
  className?: string;
  mode?: 'default' | 'hidden';
}

function Menu(props: Props) {
  const history = useHistory();
  const auth = useAppSelector(selectAuth);
  const lang = useAppSelector(selectLang);
  const urlParams = new URLSearchParams(location.search);
  const currentCategory = urlParams.get('category');

  const { className = '', mode = 'default' } = props;
  const menuItems = getMenuLinks(lang);
  const policyItems = [
    {
      name: 'termsAndConditions',
      link: generatePath(routes.TERMS_OF_USE_PAGE_ROUTE, { lang }),
    },
    {
      name: 'privacyPolicy',
      link: generatePath(routes.PRIVACY_POLICY_PAGE_ROUTE, { lang }),
    },
    {
      name: 'cyberCecurity',
      link: generatePath(routes.CYBER_SECURITY_PAGE_ROUTE, { lang }),
    },
    {
      name: 'disclaimers',
      link: generatePath(routes.DISCLAIMER_PAGE_ROUTE, { lang }),
    },
    {
      name: 'accessibilityStatetment',
      link: generatePath(routes.ACCESSIBILITY_STATETMENT_PAGE_ROUTE, { lang }),
    },
  ];

  const { translate } = useTranslation();
  const dispatch = useAppDispatch();

  const clickOnLink = () => {
    dispatch(setMenuState('hidden'));
  };

  const onMyProfileClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();

    if (!auth.isLoggedIn) {
      dispatch(setPopup({ type: 'auth', data: { initialAuthType: 'sign-in' } }));
    } else {
      history.push(generatePath(routes.USER_PROFILE_ROUTE, { lang }));
    }

    clickOnLink();
  };

  const onCategoriesClick = () => {
    dispatch(setMenuState('categories'));
  };

  return (
    <div className={cn(css.subContent, css.menu, mode === 'hidden' && css.hidden, className)}>
      <div className={css.subContentScrollable}>
        <div className={css.subContentTitle}>Menu</div>
        <span className={css.subContentItem} onClick={onCategoriesClick}>
          {translate(`menu.titles.categories`)}
        </span>
        <NavLink
          to={`${routes.MAIN_PAGE_ROUTE.replace(':lang', lang)}?category=goforisrael`}
          className={cn(css.subContentItem, currentCategory && currentCategory === 'goforisrael' && css.active)}
          onClick={() => dispatch(setMenuState('hidden'))}
        >
          goforisrael
        </NavLink>
        {menuItems?.map((item) => {
          if (item.name === 'news')
            return (
              <a href={item.link} key={`${item.link}`} className={css.subContentItem} target="_blank" rel="noreferrer">
                {translate(`menu.menuItems.${item.name}`)}
              </a>
            );
          if (item.name === 'myProfile')
            return (
              <a href="#" key={`${item.link}`} onClick={onMyProfileClick} className={css.subContentItem}>
                {translate(`menu.menuItems.${item.name}`)}
              </a>
            );
          return (
            <NavLink
              to={item.link}
              key={item.name}
              className={(isActive) => {
                return cn(css.subContentItem, { [css.active]: isActive });
              }}
              onClick={mode === 'default' ? clickOnLink : undefined}
            >
              {translate(`menu.menuItems.${item.name}`)}
            </NavLink>
          );
        })}
        <div className={css.subContentTitle}>Policy</div>
        {policyItems?.map((item) => (
          <NavLink
            to={item.link}
            className={css.subContentItem}
            onClick={mode === 'default' ? clickOnLink : undefined}
            key={item.name}
          >
            {translate(`footer.menuItems.${item.name}`)}
          </NavLink>
        ))}
      </div>
    </div>
  );
}

export { Menu };
export default Menu;
